"use client";

import {
  useDevNotifications,
  useDevUnreadCount,
  useMarkAllDevRead,
  useMarkDevRead,
} from "@/hooks/notification-hook";
import { Bell, BellRing, Check, CheckCheck, Loader2 } from "lucide-react";
import { useLocale } from "next-intl";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";

function timeAgo(iso: string, isAr: boolean) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (isAr) {
    if (m < 1) return "الآن";
    if (m < 60) return `منذ ${m} د`;
    if (h < 24) return `منذ ${h} س`;
    return `منذ ${d} ي`;
  }
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  if (h < 24) return `${h}h`;
  return `${d}d`;
}

export default function NotificationBell() {
  const locale = useLocale();
  const isAr = locale === "ar";
  const router = useRouter();
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const { data: unread } = useDevUnreadCount();
  const { data, isLoading } = useDevNotifications();
  const markOne = useMarkDevRead();
  const markAll = useMarkAllDevRead();

  const items: any[] = data?.notifications || [];
  const count = unread || 0;

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const openItem = (n: any) => {
    if (!n.read) markOne.mutate(n._id);
    if (n.link) {
      setOpen(false);
      router.push(`/${locale}${n.link}`);
    }
  };

  return (
    <div ref={ref} className="relative" dir={isAr ? "rtl" : "ltr"}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative size-10 rounded-full bg-teal-50 hover:bg-teal-100 flex items-center justify-center transition-colors"
        aria-label="notifications"
      >
        <Bell size={19} className="text-teal-700" />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className={`absolute top-full mt-2 w-[340px] max-w-[90vw] rounded-2xl overflow-hidden bg-white shadow-2xl ring-1 ring-black/5 z-50 ${isAr ? "left-0" : "right-0"}`}
        >
          {/* header */}
          <div className="relative bg-gradient-to-br from-teal-700 to-teal-900 px-5 py-4 text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BellRing size={18} className="text-amber-300" />
              <div>
                <h3 className="font-black text-sm">{isAr ? "الإشعارات" : "Notifications"}</h3>
                <p className="text-[11px] text-teal-100/70">
                  {count > 0 ? (isAr ? `${count} غير مقروء` : `${count} unread`) : isAr ? "لا جديد" : "all caught up"}
                </p>
              </div>
            </div>
            {count > 0 && (
              <button
                onClick={() => markAll.mutate()}
                className="flex items-center gap-1 text-[11px] font-bold text-amber-200 hover:text-amber-100 bg-white/10 hover:bg-white/20 rounded-full px-2.5 py-1.5"
              >
                <CheckCheck size={13} /> {isAr ? "تعليم الكل" : "Mark all"}
              </button>
            )}
          </div>

          {/* list */}
          <div className="max-h-[60vh] overflow-y-auto">
            {isLoading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="animate-spin text-teal-700" size={22} />
              </div>
            ) : items.length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-sm">
                {isAr ? "لا توجد إشعارات بعد" : "No notifications yet"}
              </div>
            ) : (
              items.map((n) => (
                <div
                  key={n._id}
                  onClick={() => openItem(n)}
                  className={`flex items-start gap-3 px-4 py-3.5 border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors relative ${
                    !n.read ? "bg-teal-50/40" : ""
                  }`}
                >
                  {!n.read && (
                    <span className="absolute top-1/2 -translate-y-1/2 start-1.5 size-2 rounded-full bg-amber-400" />
                  )}
                  <div className="size-9 rounded-xl bg-teal-100 text-teal-700 flex items-center justify-center shrink-0 ms-1.5">
                    <Bell size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-bold text-slate-800 line-clamp-1">{n.title}</p>
                    {n.body && (
                      <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{n.body}</p>
                    )}
                    <p className="text-[10px] text-slate-400 mt-1">{timeAgo(n.createdAt, isAr)}</p>
                  </div>
                  {!n.read && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        markOne.mutate(n._id);
                      }}
                      title={isAr ? "تعليم كمقروء" : "Mark as read"}
                      className="shrink-0 size-7 rounded-full bg-teal-100 text-teal-700 hover:bg-teal-600 hover:text-white flex items-center justify-center"
                    >
                      <Check size={13} />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
